import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AdminLoginHistoryRecord } from '@shared/api.interface';
import { getAdminLoginHistory } from '@/api/admin';
import { Button } from '@/components/ui/button';
import { describeUserAgent, formatDateTime } from './record-format';

const PAGE_SIZE = 20;
const cardClass = 'rounded-2xl bg-white shadow-[0_0_0_1px_var(--border)]';
const gridClass =
  'grid grid-cols-[150px_80px_minmax(0,1fr)_150px_minmax(0,1fr)] items-center gap-3 px-5';

function StatusPill({ ok }: { ok: boolean }) {
  return (
    <span
      className={
        'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ' +
        (ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700')
      }
    >
      {ok ? '成功' : '失败'}
    </span>
  );
}

export function LoginHistoryPanel() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminLoginHistoryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    const id = ++latest.current;
    setLoading(true);
    setError('');
    getAdminLoginHistory(page, PAGE_SIZE)
      .then((resp) => {
        if (id !== latest.current) return;
        setItems(resp.items);
        setTotal(resp.total);
      })
      .catch(() => {
        if (id === latest.current) setError('登录记录暂时无法读取，请重试');
      })
      .finally(() => {
        if (id === latest.current) setLoading(false);
      });
  }, [page, reload]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (error) {
    return (
      <div role="alert" className={cardClass + ' space-y-3 p-5'}>
        <p className="text-sm text-red-700">{error}</p>
        <Button variant="outline" onClick={() => setReload((n) => n + 1)}>
          重试
        </Button>
      </div>
    );
  }
  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  return (
    <div className="space-y-3.5">
      <div
        aria-busy={loading}
        className={loading ? 'opacity-60 transition-opacity' : ''}
      >
        {/* 电脑：表格 */}
        <div className={cardClass + ' hidden overflow-hidden lg:block'}>
          <div
            className={
              gridClass +
              ' h-[42px] bg-background text-[13px] font-medium text-muted-foreground'
            }
          >
            <span>时间</span>
            <span>结果</span>
            <span>设备</span>
            <span>IP</span>
            <span>失败原因</span>
          </div>
          {items.map((record) => {
            const ok = record.login_status === 'success';
            return (
              <div
                key={record.id}
                className={
                  gridClass +
                  ' min-h-[50px] border-t border-border/60 py-1.5 text-sm'
                }
              >
                <span className="tabular-nums text-accent-foreground">
                  {formatDateTime(record.created_at)}
                </span>
                <span>
                  <StatusPill ok={ok} />
                </span>
                <span
                  className="truncate"
                  title={record.user_agent || undefined}
                >
                  {describeUserAgent(record.user_agent)}
                </span>
                <span className="truncate font-mono text-[13px] text-accent-foreground">
                  {record.client_ip || '—'}
                </span>
                <span
                  className={
                    'truncate text-[13px] ' +
                    (record.fail_reason
                      ? 'text-red-700'
                      : 'text-muted-foreground')
                  }
                >
                  {record.fail_reason || '—'}
                </span>
              </div>
            );
          })}
          {items.length === 0 && (
            <p className="border-t border-border/60 py-10 text-center text-sm text-muted-foreground">
              暂无登录记录
            </p>
          )}
        </div>

        {/* 手机：卡片 */}
        <div className="space-y-2.5 lg:hidden">
          {items.map((record) => {
            const ok = record.login_status === 'success';
            return (
              <article
                key={record.id}
                className={cardClass + ' space-y-1.5 px-3.5 py-3'}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold tabular-nums">
                    {formatDateTime(record.created_at)}
                  </span>
                  <StatusPill ok={ok} />
                </div>
                <p className="break-words text-[13px] text-accent-foreground">
                  {describeUserAgent(record.user_agent)} ·{' '}
                  <span className="font-mono">{record.client_ip || '—'}</span>
                </p>
                {!ok && record.fail_reason && (
                  <p className="text-[13px] text-red-700">
                    {record.fail_reason}
                  </p>
                )}
              </article>
            );
          })}
          {items.length === 0 && (
            <p
              className={
                cardClass + ' py-8 text-center text-sm text-muted-foreground'
              }
            >
              暂无登录记录
            </p>
          )}
        </div>
      </div>

      <div
        aria-label="登录记录分页"
        className="flex flex-wrap items-center justify-between gap-3 text-sm"
      >
        <span aria-live="polite" className="text-accent-foreground">
          共 {total} 条 · 第 {page}/{totalPages} 页
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={loading || page <= 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            disabled={loading || page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}
